import { useCallback, useEffect, useState } from 'react';
import type { Mode } from '../types/agent';
import { DEFAULT_MODE, isMode } from '../services/agent/policy';

const KEY = (email: string | null) => `sculpt_mode:${email ?? 'default'}`;

/** Tool-policy mode (#5): one per user, the same in every workbook, persisted in localStorage keyed by email like
 *  the model pick (a preference, not auth). A mode kept per workbook let one "Ask" picked in one workbook follow
 *  the user into every new one while another stayed on "Auto" (2026-10-08). */
export function useMode(email: string | null): [Mode, (m: Mode) => void] {
  const [mode, setModeState] = useState<Mode>(() => read(email));

  useEffect(() => {
    setModeState(read(email));
  }, [email]);

  const setMode = useCallback(
    (m: Mode) => {
      setModeState(m);
      try {
        localStorage.setItem(KEY(email), m);
      } catch {
        /* private mode / quota — keep in memory only */
      }
    },
    [email],
  );

  return [mode, setMode];
}

function read(email: string | null): Mode {
  try {
    const v = localStorage.getItem(KEY(email));
    return isMode(v) ? v : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}
